import React, { useEffect, useState } from "react";
import { Trans, useTranslation } from "react-i18next";
import { useHistory, useLocation, useParams } from "react-router-dom";
import OrganizationLicenseAPI from "../../API/organizationLicense.api";
import organizationSubscriptionsAPI from "../../API/organizationSubscriptions.api";
import MultistepCompletionHandler from "../../components/Handlers/MultistepCompletionHandler";
import { SUBSCRIPTION_PRODUCT_TYPES } from "../../utils/constants";
import { setObject } from "../../utils/StorageHelpers";
import LogCapture from "../../utils/LogManagement/LogHelper";

const ConfirmPayment = ({ currentUser }) => {
    const history = useHistory();
    const [isSubmitting, setIsSubmitting] = useState(true);
    const [isSubmitted, setIsSubmitted] = useState(false);
    const { t } = useTranslation();

    const params = useParams();
    const location = useLocation();

    const query = new URLSearchParams(location.search);
    const subscriptionType = query.get("subscription-type");

    useEffect(() => {
        handleConfirmationRoute();
    }, []);

    const getSubscriptionTypeHandler = () => {
        switch (subscriptionType) {
            case SUBSCRIPTION_PRODUCT_TYPES.license:
                return confirmLicenseSubscription;
            case SUBSCRIPTION_PRODUCT_TYPES.product:
                return confirmProductSubscription;
            default:
                return () => {};
        }
    };

    const getSubmittedMessage = () => {
        switch (subscriptionType) {
            case SUBSCRIPTION_PRODUCT_TYPES.product:
                return t("subscriptionRequestSubmitSuccessMessageLicense");
            default:
                return t("subscriptionRequestSubmitSuccessMessage");
        }
    };

    const getRedirectRoute = () => {
        switch (subscriptionType) {
            case SUBSCRIPTION_PRODUCT_TYPES.product:
                return "/subscribed-products";
            default:
                return "/dashboard";
        }
    };

    const handleConfirmationRoute = () => {
        setIsSubmitting(true);

        const sessionId = params?.sessionId;
        const token = query.get("promotion-code");

        getSubscriptionTypeHandler()(sessionId, token);
    };

    const confirmLicenseSubscription = async (sessionId) => {
        if (sessionId) {
            const res =
                await OrganizationLicenseAPI.validateLicenseSubscription(
                    sessionId
                );

            if (res?.inserted) {
                setIsSubmitted(true);
                LogCapture.info(
                    `User: ${currentUser.email}, subscribed to license successfully`
                );
            } else {
                setIsSubmitted(false);
                LogCapture.error(
                    `User: ${currentUser.email}, failed to subscribe to license`
                );
            }
        }
        setIsSubmitting(false);
    };

    const confirmProductSubscription = async (sessionId, promotionCode) => {
        if (sessionId) {
            const res = await organizationSubscriptionsAPI.validateSubscription(
                sessionId,
                promotionCode
            );

            if (res?.inserted) {
                setIsSubmitted(true);
                LogCapture.info(
                    `User: ${currentUser.email}, subscribed to product successfully`
                );
            } else {
                setIsSubmitted(false);
                LogCapture.error(
                    `User: ${currentUser.email}, failed to subscribe to product`
                );
            }
        }
        setObject("subscription-model-filters", {}, true);
        setIsSubmitting(false);
    };

    const finalStepTitles = {
        isSubmitting: t("subscriptionRequestCreationSubmittingTitle"),
        isSubmitted: t("subscriptionRequestSubmitSuccessTitle"),
        default: t("subscriptionRequestSubmitFailTitle"),
    };

    const finalStepContent = {
        isSubmitting: t("subscriptionRequestSubmittingMessage"),
        isSubmitted: getSubmittedMessage(),
        default: (
            <span>
                <Trans i18nKey="subscriptionRequestSubmitFailMessage">
                    Your request could not be processed. Please check your
                    internet connection and try again. if you still face any
                    issue please contact us at
                </Trans>
                <b> Support@si-ware.com</b>
            </span>
        ),
    };

    return (
        <MultistepCompletionHandler
            isSubmitting={isSubmitting}
            isSubmitted={isSubmitted}
            prevStep={() => history.goBack()}
            titles={finalStepTitles}
            content={finalStepContent}
            successRedirect={getRedirectRoute()}
        />
    );
};

export default ConfirmPayment;
